<template>
  <div id="usage-report">
    <vs-popup title="Adicionar Conexões" :active.sync="instancesPopupActive" class="sizePopup">
      <table-instances
        :instances="instancesAvailable"
        :addInstancePopupActive="instancesPopupActive"
        :showTenant="true"
        confirmButtonLabel="Buscar por Conexões"
        @saveInstancesOnCustomer="searchWithSelectedInstances"
      />
    </vs-popup>

    <h3>Frequência de Uso</h3>
    <span>Frequência de uso da plataforma por tenant e conexão em um período selecionado.</span>

    <div class="vx-row mt-6">
      <div class="vx-col w-full md:w-1/5 mb-base">
        <span class="phone-number-span">Data Início</span>
        <flat-pickr
          v-model="startAt"
          v-validate="'required'"
          name="Data Início"
          placeholder="Início"
          :config="dateConfig"
        />
        <span v-show="errors.has('Data Início')" class="text-danger text-sm">{{ errors.first('Data Início') }}</span>
      </div>

      <div class="vx-col w-full md:w-1/5 mb-base">
        <span class="phone-number-span">Data Fim</span>
        <flat-pickr
          v-model="finishAt"
          v-validate="'required'"
          name="Data Fim"
          placeholder="Fim"
          :config="finishAtConfig"
        />
        <span v-show="errors.has('Data Fim')" class="text-danger text-sm">{{ errors.first('Data Fim') }}</span>
      </div>

      <div class="vx-col w-full md:w-2/5 mb-base usage-report-actions">
        <span class="phone-number-span usage-report-actions__spacer">&nbsp;</span>
        <div class="usage-report-actions__buttons">
          <vs-button @click="search" color="primary" :disabled="selectedTenants.length === 0">{{ selectAllConnections ? 'Buscar' : 'Selecionar Conexões' }}</vs-button>
          <vs-button @click="exportToExcel" color="success" :disabled="rows.length === 0">Baixar Relatório</vs-button>
        </div>
      </div>
    </div>

    <div class="usage-connections-toggle">
      <div class="usage-connections-toggle__row">
        <div class="usage-connections-toggle__label">
          <span class="phone-number-span">Selecionar todas as conexões</span>
          <vx-tooltip text="Quando esta opção está em 'Sim', a busca considera automaticamente todas as conexões dos tenants selecionados. Quando está em 'Não', você escolhe manualmente quais conexões deseja incluir no relatório antes de buscar, permitindo filtrar com precisão apenas as conexões que quer analisar.">
            <feather-icon class="ml-2 usage-connections-toggle__icon" icon="HelpCircleIcon"></feather-icon>
          </vx-tooltip>
        </div>
        <vs-switch v-model="selectAllConnections" class="usage-connections-toggle__switch">
          <span slot="on">Sim</span>
          <span slot="off">Não</span>
        </vs-switch>
      </div>
      <div class="usage-connections-toggle__divider"></div>
      <div class="usage-connections-toggle__row">
        <div class="usage-connections-toggle__label">
          <span class="phone-number-span">Apenas Tenants Ativos</span>
        </div>
        <vs-switch v-model="onlyActivesTenants" class="usage-connections-toggle__switch">
          <span slot="on">Sim</span>
          <span slot="off">Não</span>
        </vs-switch>
      </div>
    </div>

    <vs-card class="mb-6">
      <tenants-slim-list checkbox :stripe="false" :selecteds="selectedTenants" :only-actives="onlyActivesTenants" @selectedTenant="hostnamesChanged" />
    </vs-card>

    <div v-if="rows.length > 0" class="vx-row mb-6">
      <div class="vx-col w-full md:w-1/3 mb-base">
        <vs-card>
          <div class="text-center p-2">
            <p class="text-grey mb-1">Total de Conversas</p>
            <h2>{{ totalTickets }}</h2>
          </div>
        </vs-card>
      </div>
      <div class="vx-col w-full md:w-1/3 mb-base">
        <vs-card>
          <div class="text-center p-2">
            <p class="text-grey mb-1">Total de Mensagens</p>
            <h2>{{ totalMessages }}</h2>
          </div>
        </vs-card>
      </div>
      <div class="vx-col w-full md:w-1/3 mb-base">
        <vs-card>
          <div class="text-center p-2">
            <p class="text-grey mb-1">Tenants com Uso</p>
            <h2>{{ tenantsCount }}</h2>
          </div>
        </vs-card>
      </div>
    </div>

    <vs-table
      v-if="hasSearched"
      :data="paginatedRows"
      noDataText="Nenhum dado encontrado para o período selecionado."
      class="mt-4"
    >
      <div slot="header" class="usage-report-table-header">
        <div class="usage-report-search">
          <vs-input
            v-model="searchTerm"
            icon="search"
            placeholder="Pesquisar por tenant, conexão ou telefone"
            class="w-full"
          />
        </div>

        <div class="usage-report-table-header-actions">
          <vs-dropdown
            vs-trigger-click
            class="cursor-pointer usage-report-pagination-info"
          >
            <div class="usage-report-pagination-range">
              <span class="mr-2">{{ itemsPerPage }} por página</span>
              <feather-icon icon="ChevronDownIcon" svgClasses="h-4 w-4" />
            </div>
            <vs-dropdown-menu>
              <vs-dropdown-item @click="itemsPerPage = 4">
                <span>4</span>
              </vs-dropdown-item>
              <vs-dropdown-item @click="itemsPerPage = 10">
                <span>10</span>
              </vs-dropdown-item>
              <vs-dropdown-item @click="itemsPerPage = 15">
                <span>15</span>
              </vs-dropdown-item>
              <vs-dropdown-item @click="itemsPerPage = 20">
                <span>20</span>
              </vs-dropdown-item>
            </vs-dropdown-menu>
          </vs-dropdown>
        </div>
      </div>

      <template slot="thead">
        <vs-th>
          <div class="usage-report-sort-header" @click="toggleSort('tenant')">
            <span>Tenant</span>
            <span class="usage-report-sort-indicator">{{ sortIndicator('tenant') }}</span>
          </div>
        </vs-th>
        <vs-th>
          <div class="usage-report-sort-header" @click="toggleSort('connection')">
            <span>Conexão</span>
            <span class="usage-report-sort-indicator">{{ sortIndicator('connection') }}</span>
          </div>
        </vs-th>
        <vs-th>
          <div class="usage-report-sort-header" @click="toggleSort('phone')">
            <span>Telefone</span>
            <span class="usage-report-sort-indicator">{{ sortIndicator('phone') }}</span>
          </div>
        </vs-th>
        <vs-th class="usage-report-center-cell">
          <div class="usage-report-sort-header usage-report-sort-header--center" @click="toggleSort('tickets')">
            <span>Conversas</span>
            <span class="usage-report-sort-indicator">{{ sortIndicator('tickets') }}</span>
          </div>
        </vs-th>
        <vs-th class="usage-report-center-cell">
          <div class="usage-report-sort-header usage-report-sort-header--center" @click="toggleSort('messages')">
            <span>Mensagens</span>
            <span class="usage-report-sort-indicator">{{ sortIndicator('messages') }}</span>
          </div>
        </vs-th>
      </template>

      <template slot-scope="{ data }">
        <vs-tr
          v-for="(row, i) in data"
          :key="i"
          :class="i % 2 === 0 ? 'usage-row-odd' : 'usage-row-even'"
        >
          <vs-td :style="rowBackgroundStyle(i)">{{ formatTenantName(row.tenancy_name) }}</vs-td>
          <vs-td :style="rowBackgroundStyle(i)">{{ row.instance_name }}</vs-td>
          <vs-td :style="rowBackgroundStyle(i)">
            <span :class="{ 'usage-report-no-phone': !hasPhone(row) }">
              {{ formatPhoneDisplay(row) }}
            </span>
          </vs-td>
          <vs-td :style="rowBackgroundStyle(i)" class="usage-report-center-cell">
            <div class="usage-report-cell-center">{{ row.ticket_count }}</div>
          </vs-td>
          <vs-td :style="rowBackgroundStyle(i)" class="usage-report-center-cell">
            <div class="usage-report-cell-center">{{ row.message_count }}</div>
          </vs-td>
        </vs-tr>
      </template>
    </vs-table>

    <div v-if="hasSearched && filteredRows.length > 0" class="mt-3 usage-report-pagination-wrapper">
      <div class="usage-report-pagination-range usage-report-pagination-range--footer">
        <span>Página {{ page }} de {{ totalPages || 1 }}</span>
      </div>

      <vs-pagination
        :max="7"
        :total="totalPages"
        v-model="page"
      />
    </div>
  </div>
</template>

<script>
import ExcelJS from 'exceljs'
import { mapGetters } from 'vuex'
import flatPickr from 'vue-flatpickr-component'
import { Portuguese } from 'flatpickr/dist/l10n/pt.js'
import 'flatpickr/dist/flatpickr.css'
import tenantsHttp from '@/http/requests/tenants'
import TenantsSlimList from '@/views/Tenants/TenantsSlimList.vue'
import TableInstances from '@/views/users/TableInstances.vue'

export default {
  components: { flatPickr, TenantsSlimList, TableInstances },
  data() {
    return {
      startAt: '',
      finishAt: '',
      selectedTenants: [],
      selectAllConnections: true,
      onlyActivesTenants: true,
      instancesPopupActive: false,
      instancesAvailable: [],
      rows: [],
      hasSearched: false,
      page: 1,
      itemsPerPage: 10,
      searchTerm: '',
      sortKey: 'tenant',
      sortDirection: 'asc',
      dateConfig: {
        enableTime: false,
        dateFormat: 'd/m/Y',
        maxDate: 'today',
        locale: Portuguese
      }
    }
  },
  computed: {
    ...mapGetters(['token', 'tenants']),
    finishAtConfig() {
      return { ...this.dateConfig, minDate: this.startAt, maxDate: 'today' }
    },
    startAtISO() {
      return this.toISO(this.startAt)
    },
    finishAtISO() {
      return this.toISO(this.finishAt)
    },
    totalTickets() {
      return this.rows.reduce((sum, r) => sum + (r.ticket_count || 0), 0)
    },
    totalMessages() {
      return this.rows.reduce((sum, r) => sum + (r.message_count || 0), 0)
    },
    tenantsCount() {
      return new Set(this.rows.map(r => r.tenancy_id)).size
    },
    totalPages() {
      if (!this.filteredRows.length) return 0
      return Math.ceil(this.filteredRows.length / this.itemsPerPage)
    },
    pageStart() {
      if (!this.filteredRows.length) return 0
      return (this.page - 1) * this.itemsPerPage + 1
    },
    pageEnd() {
      if (!this.filteredRows.length) return 0
      return Math.min(this.page * this.itemsPerPage, this.filteredRows.length)
    },
    sortedRows() {
      const rows = [...this.rows]

      return rows.sort((a, b) => {
        const multiplier = this.sortDirection === 'asc' ? 1 : -1
        let primary = 0

        if (this.sortKey === 'tenant') {
          primary = this.compareNatural(this.formatTenantName(a.tenancy_name), this.formatTenantName(b.tenancy_name))
        } else if (this.sortKey === 'connection') {
          primary = this.compareNatural(a.instance_name, b.instance_name)
        } else if (this.sortKey === 'phone') {
          primary = this.comparePhone(a.phone_number, b.phone_number)
        } else if (this.sortKey === 'tickets') {
          primary = this.compareNumber(a.ticket_count, b.ticket_count)
        } else if (this.sortKey === 'messages') {
          primary = this.compareNumber(a.message_count, b.message_count)
        }

        if (primary !== 0) return primary * multiplier

        return this.comparePhone(a.phone_number, b.phone_number)
      })
    },
    filteredRows() {
      const term = String(this.searchTerm || '').trim().toLowerCase()
      if (!term) return this.sortedRows

      return this.sortedRows.filter((row) => {
        const tenant = String(this.formatTenantName(row.tenancy_name) || '').toLowerCase()
        const connection = String(row.instance_name || '').toLowerCase()
        const phoneRaw = String(row.phone_number || '')
        const phoneFormatted = String(this.formatPhone(row.phone_number) || '').toLowerCase()
        const phoneDigits = phoneRaw.replace(/\D/g, '')
        const termDigits = term.replace(/\D/g, '')

        return (
          tenant.includes(term) ||
          connection.includes(term) ||
          phoneFormatted.includes(term) ||
          (termDigits && phoneDigits.includes(termDigits))
        )
      })
    },
    paginatedRows() {
      const start = (this.page - 1) * this.itemsPerPage
      const end = start + this.itemsPerPage
      return this.filteredRows.slice(start, end)
    },
    selectedTenantNames() {
      return this.selectedTenants.reduce((names, selectedId) => {
        const tenant = this.tenants.find(item => String(item.id) === String(selectedId))
        if (!tenant || !tenant.fqdn) return names

        return names.concat([tenant.fqdn])
      }, []).filter((name, index, names) => names.indexOf(name) === index)
    }
  },
  watch: {
    rows() {
      this.page = 1
    },
    searchTerm() {
      this.page = 1
    },
    itemsPerPage() {
      this.page = 1
    },
    totalPages(newTotal) {
      if (newTotal === 0) {
        this.page = 1
        return
      }

      if (this.page > newTotal) {
        this.page = newTotal
      }
    }
  },
  methods: {
    toggleSort(key) {
      if (this.sortKey === key) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
        return
      }

      this.sortKey = key
      this.sortDirection = 'asc'
    },
    sortIndicator(key) {
      if (this.sortKey !== key) return ''
      return this.sortDirection === 'asc' ? '↓' : '↑'
    },
    compareNatural(a, b) {
      return String(a || '').localeCompare(String(b || ''), 'pt-BR', { numeric: true, sensitivity: 'base' })
    },
    compareNumber(a, b) {
      return Number(a || 0) - Number(b || 0)
    },
    comparePhone(a, b) {
      const pa = Number(String(a || '').replace(/\D/g, '') || 0)
      const pb = Number(String(b || '').replace(/\D/g, '') || 0)
      return pa - pb
    },
    formatTenantName(tenantName) {
      const name = String(tenantName || '').trim()
      if (!name) return ''
      return name.split('.')[0]
    },
    rowBackgroundStyle(index) {
      return {
        background: index % 2 === 0 ? '#ffffff' : '#9e9e9e22'
      }
    },
    hostnamesChanged(selected) {
      this.selectedTenants = selected || []
      this.instancesAvailable = []
      this.rows = []
      this.hasSearched = false
      this.searchTerm = ''
    },
    isSocialChannel(instanceName) {
      const name = String(instanceName || '').toLowerCase()
      return /facebook|instagram|messenger|telegram|rcs/.test(name)
    },
    hasPhone(row) {
      return !!String(row.phone_number || '').trim()
    },
    formatPhone(phone) {
      if (!phone) return ''
      const cleaned = String(phone).replace(/\D/g, '')
      const local = cleaned.startsWith('55') && cleaned.length >= 12 ? cleaned.slice(2) : cleaned
      if (local.length === 11) return `(${local.slice(0, 2)}) ${local.slice(2, 7)}-${local.slice(7)}`
      if (local.length === 10) return `(${local.slice(0, 2)}) ${local.slice(2, 6)}-${local.slice(6)}`
      return phone
    },
    formatPhoneDisplay(row) {
      const phone = String(row.phone_number || '').trim()
      if (phone) return this.formatPhone(phone)
      if (this.isSocialChannel(row.instance_name)) return 'N/A (integração)'
      return '—'
    },
    formatPhoneForExport(row) {
      const phone = String(row.phone_number || '').trim()
      if (phone) return this.formatPhone(phone)
      if (this.isSocialChannel(row.instance_name)) return 'N/A (integracao)'
      return 'Sem telefone'
    },
    async exportToExcel() {
      const workbook = new ExcelJS.Workbook()
      const worksheet = workbook.addWorksheet('Frequência de Uso')

      worksheet.columns = [
        { key: 'tenant' },
        { key: 'connection' },
        { key: 'phone', numFmt: '@' },
        { key: 'tickets' },
        { key: 'messages' }
      ]

      worksheet.addRow(['Tenant', 'Conexão', 'Telefone', 'Conversas', 'Mensagens'])

      this.rows.forEach(row => {
        const phoneValue = this.formatPhoneForExport(row)
        const wsRow = worksheet.addRow([
          this.formatTenantName(row.tenancy_name),
          row.instance_name,
          phoneValue,
          row.ticket_count,
          row.message_count
        ])
        wsRow.getCell(3).value = phoneValue
        wsRow.getCell(3).numFmt = '@'
      })

      const blob = await workbook.xlsx.writeBuffer()
      const blobObject = new Blob([blob], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const url = window.URL.createObjectURL(blobObject)
      const a = document.createElement('a')
      a.href = url
      a.download = `frequencia_uso_${this.startAtISO}_${this.finishAtISO}.xlsx`
      a.style.display = 'none'
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    },
    toISO(date) {
      if (!date) return ''
      const [d, m, y] = String(date).split('/')
      return `${y}-${m}-${d}`
    },
    async fetchUsageReport(instanceIds = []) {
      return tenantsHttp.getUsageReport(
        this.token,
        this.startAtISO,
        this.finishAtISO,
        {
          hostnameIds: this.selectedTenants,
          tenantNames: this.selectedTenantNames,
          instanceIds
        }
      )
    },
    async fetchInstancesAvailable() {
      const shortTenantNames = this.selectedTenantNames.map(fqdn => this.formatTenantName(fqdn))
      const responses = await Promise.allSettled(
        shortTenantNames.map(tenantName => tenantsHttp.getInstances(tenantName, this.token))
      )

      const instances = []
      const failedTenants = []
      responses.forEach((response, index) => {
        if (response.status !== 'fulfilled') {
          failedTenants.push(shortTenantNames[index])
          return
        }
        const tenancyName = shortTenantNames[index]

        ;(response.value.data || []).forEach(instance => {
          instances.push({
            id: null,
            instance: {
              id: instance.id,
              name: instance.name,
              activated: instance.activated,
              tenancy_name: tenancyName
            }
          })
        })
      })

      this.instancesAvailable = instances

      if (failedTenants.length > 0) {
        this.$vs.notify({
          color: 'warning',
          title: 'Falha ao carregar conexões',
          text: `Não foi possível buscar conexões de: ${failedTenants.join(', ')}.`
        })
      }
    },
    async openInstancesPopup() {
      if (this.selectedTenantNames.length === 0) return

      try {
        await this.fetchInstancesAvailable()

        if (this.instancesAvailable.length === 0) {
          this.$vs.notify({ color: 'warning', title: 'Sem conexões', text: 'Nenhuma conexão encontrada para o tenant selecionado.' })
          return
        }

        this.instancesPopupActive = true
      } catch (err) {
        this.$vs.notify({ color: 'danger', title: 'Erro', text: (err && err.response && err.response.data && err.response.data.message) || 'Erro ao buscar conexões.' })
      }
    },
    searchWithSelectedInstances(selectedInstances) {
      const instanceIds = selectedInstances.map(({ instance }) => instance.id)
      this.instancesPopupActive = false

      if (instanceIds.length === 0) {
        this.$vs.notify({ color: 'warning', title: 'Conexão obrigatória', text: 'Selecione pelo menos uma conexão para buscar.' })
        return
      }

      this.runSearch(instanceIds, selectedInstances)
    },
    async runSearch(instanceIds = [], selectedInstances = []) {
      try {
        const res = await this.fetchUsageReport(instanceIds)
        this.hasSearched = true

        let rows = res.data
        if (selectedInstances.length > 0) {
          const validKeys = new Set(
            selectedInstances.map(({ instance }) => `${instance.id}__${instance.name}`)
          )
          rows = rows.filter(row => validKeys.has(`${row.instance_id}__${row.instance_name}`))
        }

        // Deduplicate rows caused by historical instance renames in the backend.
        // Same (tenancy_id, instance_id, phone) produces identical counts — keep first.
        const seen = new Set()
        rows = rows.filter(row => {
          const key = `${row.tenancy_id}__${row.instance_id}__${String(row.phone_number || '').trim()}`
          if (seen.has(key)) return false
          seen.add(key)
          return true
        })

        this.rows = rows
        if (this.rows.length === 0) {
          this.$vs.notify({ color: 'warning', title: 'Sem dados', text: 'Nenhum dado encontrado para o período selecionado.' })
        }
      } catch (err) {
        this.$vs.notify({ color: 'danger', title: 'Erro', text: (err && err.response && err.response.data && err.response.data.message) || 'Erro ao buscar dados.' })
      }
    },
    async search() {
      const valid = await this.$validator.validateAll()
      if (!valid) return

      if (this.selectedTenants.length === 0) {
        this.rows = []
        this.hasSearched = false
        this.$vs.notify({ color: 'warning', title: 'Tenant obrigatório', text: 'Selecione pelo menos um tenant para buscar.' })
        return
      }

      if (this.selectedTenantNames.length === 0) {
        this.rows = []
        this.hasSearched = false
        this.$vs.notify({ color: 'danger', title: 'Erro', text: 'Tenant selecionado não encontrado.' })
        return
      }

      if (this.selectAllConnections) {
        this.runSearch()
        return
      }

      this.openInstancesPopup()
    }
  },
  async beforeMount() {
    if (!this.$store.getters.tenants || !this.$store.getters.tenants.length) {
      await this.$store.dispatch('getAllTenants', { token: this.token })
    }
  },
  beforeRouteLeave(to, from, next) {
    if (this.instancesPopupActive) {
      this.instancesPopupActive = false
      setTimeout(() => {
        next()
      }, 1)
    } else {
      next()
    }
  }
}
</script>

<style scoped>
.usage-report-actions__spacer {
  visibility: hidden;
  display: block;
}

.usage-report-actions__buttons {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.usage-connections-toggle {
  margin: 8px 0 18px;
  padding: 10px 14px;
  border: 1px solid #e8e8ef;
  border-radius: 10px;
  background: #fafafe;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.usage-connections-toggle__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 4px 0;
}

.usage-connections-toggle__divider {
  height: 1px;
  background: #e8e8ef;
  margin: 6px 0;
}

.usage-connections-toggle__label {
  display: flex;
  align-items: center;
}

.usage-connections-toggle__icon {
  cursor: pointer;
}

.usage-connections-toggle__switch {
  margin-left: auto;
}

.usage-report-pagination-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.usage-report-table-header {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.usage-report-search {
  width: 100%;
  max-width: 360px;
}

.usage-report-table-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.usage-report-pagination-range {
  padding: 10px 12px;
  border: 1px solid #e8e8ef;
  border-radius: 999px;
  background: #fff;
  display: flex;
  align-items: center;
}

.usage-report-pagination-range--footer {
  padding: 10px 12px;
  border: 1px solid #e8e8ef;
  border-radius: 999px;
  background: #fff;
  white-space: nowrap;
  line-height: 1;
  min-width: max-content;
}

.usage-report-pagination-info {
  margin-right: 0;
}

.usage-report-center-cell {
  text-align: center !important;
}

.usage-report-cell-center {
  width: 100%;
  display: flex;
  justify-content: center;
  text-align: center;
}

.usage-report-sort-header {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  user-select: none;
}

.usage-report-sort-header--center {
  justify-content: center;
  width: 100%;
}

.usage-report-sort-indicator {
  min-width: 10px;
  font-weight: 600;
}

.usage-report-no-phone {
  color: #aaa;
  font-style: italic;
  font-size: 0.82em;
}

::v-deep #usage-report .vs-table--thead th:nth-child(4),
::v-deep #usage-report .vs-table--thead th:nth-child(5) {
  text-align: center !important;
}

::v-deep #usage-report .vs-table--thead th:nth-child(4) .vs-table-text,
::v-deep #usage-report .vs-table--thead th:nth-child(5) .vs-table-text {
  justify-content: center !important;
  width: 100%;
}

::v-deep #usage-report .vs-table--tbody td:nth-child(4),
::v-deep #usage-report .vs-table--tbody td:nth-child(5) {
  text-align: center !important;
  padding-left: 0 !important;
  padding-right: 0 !important;
}

::v-deep .vs-pagination--ul {
  width: fit-content !important;
}

::v-deep .vs-pagination--li {
  margin-left: 0.35rem;
  margin-right: 0.35rem;
}

</style>
